import config from '@/configs/navigation/business-admin.json';

/** Merchant administration is a staff area, distinct from the platform /admin principal. */
export const BUSINESS_ADMIN_MOUNT = '/dashboard/admin';
export const BUSINESS_ADMIN_ACCESS_API = '/api/dashboard/admin-access';
export const BUSINESS_ADMIN_SECTIONS = config.sections;
export type BusinessAdminSection = (typeof BUSINESS_ADMIN_SECTIONS)[number]['id'];
export type BusinessAdminAccess = { businessId: string | null; isOwner: boolean; sections: string[] };

export function isBusinessAdminPath(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0];
  return path === BUSINESS_ADMIN_MOUNT || path.startsWith(`${BUSINESS_ADMIN_MOUNT}/`);
}

export function businessAdminSectionsFor(access: {
  isOwner: boolean;
  hasAccess: boolean;
  fullBusinessScope: boolean;
  hasCapability: (key: string) => boolean;
}): string[] {
  if (!access.hasAccess) return [];
  if (access.isOwner) return BUSINESS_ADMIN_SECTIONS.map((section) => section.id);
  // These pages use business-wide aggregates/configuration. Branch-limited
  // members keep their existing Workspace tools, without an aggregate-data leak.
  if (!access.fullBusinessScope) return [];
  return BUSINESS_ADMIN_SECTIONS.filter((section) => !('ownerOnly' in section && section.ownerOnly) && access.hasCapability(section.capability)).map((section) => section.id);
}

/** Closed page registry: no arbitrary paths or dynamic imports from URL input. */
export function resolveBusinessAdminPage(segments: string[]) {
  const [section, ...rest] = segments;
  if (!section) return null;
  const tail = rest.join('/');
  if (section === 'reports' && !tail) return { section, page: 'reports', params: {} };
  if (section === 'reports' && tail === 'revenue') return { section, page: 'revenue', params: {} };
  if (section === 'branches' && tail === 'profile') return { section, page: 'branch-profile', params: {} };
  if (section === 'settings' && ['payment-methods','modes','pos','terminals','features'].includes(tail)) return { section, page: tail, params: {} };
  if (section === 'branches' && !tail) return { section, page: 'branches', params: {} };
  if (section === 'settings' && !tail) return { section, page: 'settings', params: {} };
  if (section === 'settings' && tail === 'business-profile') return { section, page: 'business-profile', params: {} };
  if (section === 'settings' && tail === 'configuration') return { section, page: 'configuration', params: {} };
  if (section === 'billing' && !tail) return { section, page: 'billing', params: {} };
  if (section === 'team') {
    if (!tail) return { section, page: 'team', params: {} };
    if (['invite','audit','policies'].includes(tail)) return { section, page: `team-${tail}`, params: {} };
    if (rest[0] === 'policies' && rest.length === 2) return { section, page: rest[1] === 'new' ? 'policy-new' : 'policy-detail', params: { id: rest[1] } };
    if (rest[0] === 'members' && rest.length === 2) return { section, page: 'member-detail', params: { id: rest[1] } };
  }
  return null;
}

/** Legacy pages remain reachable in Workspace; when used inside Admin their
 * internal dashboard links stay in the same area if that destination exists. */
export function adminEquivalentPath(path: string): string | null {
  const url = new URL(path, 'https://local.invalid');
  const prefixes: Array<[string, string]> = [
    ['/dashboard/operations/payment-methods', '/settings/payment-methods'],
    ['/dashboard/operations/modes', '/settings/modes'], ['/dashboard/operations/pos', '/settings/pos'],
    ['/dashboard/operations/terminals', '/settings/terminals'], ['/dashboard/operations/capabilities', '/settings/features'],
    ['/dashboard/branch-profile', '/branches/profile'],
    ['/dashboard/analytics', '/reports'], ['/dashboard/branches', '/branches'],
    ['/dashboard/team', '/team'], ['/dashboard/settings', '/settings'],
    ['/dashboard/business-profile', '/settings/business-profile'],
    ['/dashboard/manage/settings', '/settings/configuration'], ['/dashboard/billing', '/billing'],
    ['/dashboard/ai/pools', '/ai'],
  ];
  for (const [old, target] of prefixes) {
    const candidate = url.pathname.startsWith('/dashboard') ? url.pathname : `/dashboard${url.pathname}`;
    if (candidate !== old && !candidate.startsWith(`${old}/`)) continue;
    const relative = `${target}${candidate.slice(old.length)}`;
    if (!resolveBusinessAdminPage(relative.split('/').filter(Boolean))) return null;
    return `${BUSINESS_ADMIN_MOUNT}${relative}${url.search}${url.hash}`;
  }
  return null;
}

/** Workspace keeps operational and personal destinations. The admin sidebar
 * uses the same section registry, but management roots do not crowd this list. */
export function filterWorkspaceNavigation<T extends { items: Array<{ href: string; name: string; children?: Array<{ href: string; name: string }> }> }>(groups: T[], allowedSections: readonly string[] = BUSINESS_ADMIN_SECTIONS.map((section) => section.id)): T[] {
  const movedToAdmin = (href: string) => {
    const equivalent = adminEquivalentPath(href);
    return equivalent && allowedSections.includes(equivalent.slice(BUSINESS_ADMIN_MOUNT.length + 1).split(/[/?#]/)[0]);
  };
  return groups.map((group) => ({ ...group, items: group.items.flatMap((item) => {
    const management = movedToAdmin(item.href) ? adminEquivalentPath(item.href) : null;
    const children = item.children?.filter((child) => !movedToAdmin(child.href));
    if (!management) return [{ ...item, ...(item.children ? { children } : {}) }];
    if (!children?.length) return [];
    const name = management.includes('/team') ? 'Staff work' : management.includes('/branches') ? 'Branch tools' : 'My account';
    return [{ ...item, name, href: children[0].href, children }];
  }) })).filter((group) => group.items.length) as T[];
}
